<template>
    <BaseModal
        v-model="showModal"
        title="Remboursement"
        size="3xl"
        :closable="!isProcessing"
        @close="$emit('close')"
    >
        <template #header>
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500">
                    <i class="fas fa-rotate-left text-lg"></i>
                </div>
                <div>
                    <h2 class="text-lg font-semibold text-gray-900">Remboursement de la vente</h2>
                    <p class="text-xs text-gray-400">{{ sale.invoice_number }}</p>
                </div>
            </div>
        </template>

        <div class="space-y-5">
            <!-- Message d'erreur -->
            <div v-if="errorMessage" class="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-2.5">
                <i class="fas fa-exclamation-circle text-red-500 mt-0.5 text-sm flex-shrink-0"></i>
                <span class="text-sm text-red-600">{{ errorMessage }}</span>
            </div>

            <!-- Type de remboursement -->
            <div>
                <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Type de remboursement</h4>
                <div class="grid grid-cols-2 gap-3">
                    <label
                        class="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all"
                        :class="!isPartial ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
                    >
                        <input type="radio" :value="false" v-model="isPartial" class="mt-0.5 accent-primary">
                        <div>
                            <span class="text-sm font-semibold text-gray-900 block">Remboursement total</span>
                            <span class="text-xs text-gray-400">Rembourser tous les articles</span>
                        </div>
                    </label>
                    <label
                        class="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all"
                        :class="isPartial ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
                    >
                        <input type="radio" :value="true" v-model="isPartial" class="mt-0.5 accent-primary">
                        <div>
                            <span class="text-sm font-semibold text-gray-900 block">Remboursement partiel</span>
                            <span class="text-xs text-gray-400">Rembourser certains articles</span>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Liste des articles (pour remboursement partiel) -->
            <div v-if="isPartial">
                <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Articles a rembourser</h4>
                <div class="space-y-2">
                    <div v-for="item in saleItems" :key="item.id"
                        class="flex items-center justify-between gap-4 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                        <!-- Info produit -->
                        <div class="flex items-center gap-3 flex-1 min-w-0">
                            <div class="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                                <img v-if="item.product.image_url" :src="item.product.image_url"
                                    :alt="item.product.name" class="w-full h-full object-cover">
                                <i v-else class="fas fa-image text-gray-400 text-xs"></i>
                            </div>
                            <div class="min-w-0">
                                <p class="text-sm font-medium text-gray-900 truncate">{{ item.product.name }}</p>
                                <p class="text-xs text-gray-400">{{ formatCurrency(item.unit_price) }} x {{ item.quantity }}</p>
                            </div>
                        </div>

                        <!-- Quantite -->
                        <div class="flex flex-col items-end gap-1 flex-shrink-0">
                            <span class="text-[10px] text-gray-400">Qte a rembourser</span>
                            <div class="flex items-center gap-1">
                                <button
                                    class="w-7 h-7 flex items-center justify-center border border-gray-200 bg-white rounded text-gray-500 font-semibold text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    @click="decrementQuantity(item.id)"
                                    :disabled="getRefundQuantity(item.id) === 0">-</button>
                                <input
                                    type="number"
                                    :value="getRefundQuantity(item.id)"
                                    @input="updateQuantity(item.id, $event)"
                                    :max="item.quantity"
                                    min="0"
                                    class="w-11 h-7 text-center border border-gray-200 rounded text-sm focus:outline-none focus:border-primary">
                                <button
                                    class="w-7 h-7 flex items-center justify-center border border-gray-200 bg-white rounded text-gray-500 font-semibold text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    @click="incrementQuantity(item.id)"
                                    :disabled="getRefundQuantity(item.id) >= item.quantity">+</button>
                            </div>
                            <span class="text-[10px] text-gray-300">Max: {{ item.quantity }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Recapitulatif -->
            <div class="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Recapitulatif</h4>
                <div class="space-y-2">
                    <div class="flex justify-between text-sm text-gray-500">
                        <span>Montant total de la vente</span>
                        <span>{{ formatCurrency(totalAmount) }}</span>
                    </div>
                    <div class="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-100">
                        <span>Montant a rembourser</span>
                        <span class="text-red-600">{{ formatCurrency(refundAmount) }}</span>
                    </div>
                    <div v-if="isPartial" class="flex justify-between text-sm text-gray-500">
                        <span>Articles a rembourser</span>
                        <span>{{ selectedItemsCount }}</span>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex items-center justify-end gap-3 w-full">
                <BaseButton variant="ghost" :disabled="isProcessing" @click="$emit('close')">
                    Annuler
                </BaseButton>
                <BaseButton
                    variant="danger"
                    :loading="isProcessing"
                    :disabled="!canProcess"
                    @click="processRefund"
                >
                    Confirmer le remboursement
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { salesService } from '@/services';
import CashRegisterService from '@/services/cashRegisterService';
import { useToastStore } from '@/store/modules/toastStore';
import BaseModal from '@/components/base/BaseModal.vue';
import BaseButton from '@/components/base/BaseButton.vue';

export default {
    name: 'RefundModal',
    components: {
        BaseModal,
        BaseButton
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        sale: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'refunded'],
    setup(props, { emit }) {
        const toastStore = useToastStore();
        const errorMessage = ref('');
        const isPartial = ref(false);
        const refundItems = ref([]);
        const cashRegisterId = ref('');
        const isProcessing = ref(false);

        const showModal = computed({
            get: () => props.show,
            set: (value) => {
                if (!value) {
                    emit('close');
                }
            }
        });

        const saleItems = computed(() => props.sale.sale_items || []);
        const totalAmount = computed(() => parseFloat(props.sale.total_incl_tax || 0));

        const refundAmount = computed(() => {
            if (!isPartial.value) {
                return totalAmount.value;
            }

            let total = 0;
            refundItems.value.forEach(refundItem => {
                const saleItem = saleItems.value.find(item => item.id === refundItem.sale_item_id);
                if (saleItem && refundItem.quantity > 0) {
                    const refundRatio = refundItem.quantity / saleItem.quantity;
                    total += saleItem.total_amount * refundRatio;
                }
            });
            return total;
        });

        const selectedItemsCount = computed(() => {
            return refundItems.value.filter(item => item.quantity > 0).length;
        });

        const canProcess = computed(() => {
            if (isPartial.value) {
                return selectedItemsCount.value > 0 && refundAmount.value > 0;
            }
            return true;
        });

        const initializeRefundItems = () => {
            refundItems.value = saleItems.value.map(item => ({
                sale_item_id: item.id,
                quantity: 0
            }));
        };

        const getRefundQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            return refundItem ? refundItem.quantity : 0;
        };

        const updateQuantity = (itemId, event) => {
            const value = parseInt(event.target.value) || 0;
            const saleItem = saleItems.value.find(item => item.id === itemId);
            const maxQuantity = saleItem ? saleItem.quantity : 0;

            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            if (refundItem) {
                refundItem.quantity = Math.max(0, Math.min(value, maxQuantity));
            }
        };

        const incrementQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            const saleItem = saleItems.value.find(item => item.id === itemId);
            if (refundItem && saleItem && refundItem.quantity < saleItem.quantity) {
                refundItem.quantity++;
            }
        };

        const decrementQuantity = (itemId) => {
            const refundItem = refundItems.value.find(item => item.sale_item_id === itemId);
            if (refundItem && refundItem.quantity > 0) {
                refundItem.quantity--;
            }
        };

        const processRefund = async () => {
            if (!canProcess.value) return;

            try {
                const cashRegister = await CashRegisterService.getMyOpenedCashRegister();
                cashRegisterId.value = cashRegister.data.id;
            } catch (error) {
                errorMessage.value = error.message;
                return;
            }

            try {
                isProcessing.value = true;

                const refundData = {
                    partial: isPartial.value,
                    cash_register_id: cashRegisterId.value
                };

                if (isPartial.value) {
                    refundData.items = refundItems.value.filter(item => item.quantity > 0);
                }

                const response = await salesService.refundSale(props.sale.id, refundData);
                console.log('response', response.data);

                emit('refunded');
                emit('close');
            } catch (error) {
                console.error('Erreur remboursement:', error);

                const errMsg = error.response?.data?.message || 'Erreur remboursement';
                toastStore.addToast({
                    type: 'error',
                    message: errMsg,
                });
            } finally {
                isProcessing.value = false;
            }
        };

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0);
        };

        const formatDate = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        };

        watch(() => props.show, (newVal) => {
            if (newVal) {
                initializeRefundItems();
                isPartial.value = false;
                cashRegisterId.value = '';
                errorMessage.value = '';
            }
        });

        watch(isPartial, () => {
            initializeRefundItems();
        });

        return {
            showModal,
            isPartial,
            refundItems,
            cashRegisterId,
            isProcessing,
            saleItems,
            totalAmount,
            refundAmount,
            selectedItemsCount,
            canProcess,
            errorMessage,
            getRefundQuantity,
            updateQuantity,
            incrementQuantity,
            decrementQuantity,
            processRefund,
            formatCurrency,
            formatDate
        };
    }
};
</script>
